// Cash payment section. Leaving the amount empty (or below total) records the
// shortfall as customer debt — no reason needed, but a customer must be selected.
import { useState, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/ui/money-input";

const fmtVND = (n: number) => new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
const QUICK_AMOUNTS = [50000, 100000, 200000, 500000, 1000000];

interface PaymentCashSectionProps {
  total: number;
  hasCustomer: boolean;
  couponCode?: string;
  onConfirm: (tenderedAmount: number, couponCode?: string, reason?: string) => void;
  onBack: () => void;
  loading?: boolean;
  /** "cash" (tiền mặt) hoặc "bank" (chuyển khoản thủ công). Chỉ đổi nhãn hiển thị. */
  mode?: "cash" | "bank";
}

export function PaymentCashSection({ total, hasCustomer, couponCode, onConfirm, onBack, loading, mode = "cash" }: PaymentCashSectionProps) {
  const isBank = mode === "bank";
  const title = isBank ? "Thanh toán chuyển khoản" : "Thanh toán tiền mặt";
  const amountLabel = isBank ? "Số tiền đã chuyển" : "Tiền khách đưa";
  // Empty by default: not filling it in means "owe the whole bill".
  const [tendered, setTendered] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTendered("");
    setTimeout(() => { inputRef.current?.focus(); }, 60);
  }, [total]);

  const tenderedNum = tendered.trim() === "" ? 0 : (parseFloat(tendered) || 0);
  const shortfall = Math.max(0, total - tenderedNum);
  const change = Math.max(0, tenderedNum - total);
  const isDebt = shortfall > 0;
  // Debt must belong to a customer; otherwise confirming is always allowed.
  const canConfirm = !loading && (!isDebt || hasCustomer);

  const handleConfirm = () => {
    if (canConfirm) onConfirm(tenderedNum, couponCode, undefined);
  };

  // Keyboard: Enter = confirm, Esc = back.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onBack();
      if (e.key === "Enter" && canConfirm && !loading) handleConfirm();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, canConfirm]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={onBack} disabled={loading} className="shrink-0">
          <ArrowLeft size={16} />
        </Button>
        <span className="font-semibold text-base">{title}</span>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tổng cộng</span>
          <span className="text-xl font-bold text-primary">{fmtVND(total)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <label className="text-muted-foreground shrink-0">{amountLabel}</label>
          <MoneyInput
            ref={inputRef}
            value={tendered}
            onChange={setTendered}
            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            placeholder="Để trống = nợ toàn bộ"
            className="font-mono text-base w-48"
          />
        </div>

        {/* Debt notice when tendered < total (including empty) */}
        {isDebt && (
          <div className={`rounded-md border px-3 py-2 ${hasCustomer ? "border-amber-300 bg-amber-50" : "border-destructive/30 bg-destructive/5"}`}>
            {hasCustomer ? (
              <p className="text-sm text-amber-800 font-medium">
                Khách nợ {fmtVND(shortfall)}{tenderedNum > 0 ? ` (đã trả ${fmtVND(tenderedNum)})` : " (chưa trả)"}
              </p>
            ) : (
              <p className="text-xs text-destructive">Chọn khách hàng để ghi nợ phần còn thiếu.</p>
            )}
          </div>
        )}

        <div className="flex justify-between border-t pt-3">
          <span className="text-muted-foreground">{isDebt ? "Ghi nợ" : "Tiền thừa"}</span>
          <span className={`text-base font-bold ${isDebt ? "text-amber-600" : "text-green-600"}`}>
            {isDebt ? fmtVND(shortfall) : fmtVND(change)}
          </span>
        </div>
      </div>

      {/* Quick-amount + exact buttons */}
      <div className="flex flex-wrap gap-2">
        {QUICK_AMOUNTS.map((amt) => {
          const rounded = amt >= total ? amt : Math.ceil(total / amt) * amt;
          return (
            <Button key={amt} variant="outline" size="sm" onClick={() => setTendered(String(rounded))} className="text-xs">
              {fmtVND(rounded)}
            </Button>
          );
        })}
        <Button variant="outline" size="sm" onClick={() => setTendered(String(Math.round(total)))} className="text-xs">Đủ</Button>
      </div>

      <div className="flex flex-col gap-2 pt-1">
        <Button onClick={handleConfirm} disabled={!canConfirm || loading} className="w-full font-semibold">
          {loading ? "Đang xử lý..." : "Xác nhận (↵)"}
        </Button>
        <Button variant="outline" onClick={onBack} disabled={loading} className="w-full">Quay lại (Esc)</Button>
      </div>
    </div>
  );
}
